import Card from "@/components/Card";
import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import SkeletonRow from "@/components/SkeletonRow";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { web } from "@/config/System";
import useApiBaseUrl from "@/hooks/useApiBaseUrl";
import usePaymentMethod from "@/hooks/usePaymentMethod";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import {
  formatDateIndonesia,
  formatRupiah,
  getPaymentStatusInfo,
} from "@/lib/utils";
import { getBiodata } from "@/services/getData";
import {
  DataPendaftaran,
  DataSiswa,
  DataTagihanDaftarPlaza,
  PaymentMethod,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  Banknote,
  Calendar,
  CheckCircle2,
  Clock,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  HelpCircle,
  Info,
  Loader2,
  Printer,
  Receipt,
  Ticket,
} from "lucide-react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";

// ... (API functions tetap sama) ...
const getInformasiTagihan = async (id_sys_periode: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/validasi/informasi-tagihan?id_sys_periode=${id_sys_periode}`,
  );
  return response.data?.data;
};
const getTagihanExpired = async (
  id_sys_periode: string,
  id_data_pendaftaran: string,
) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/validasi/cek-expired-date-tagihan`,
    { params: { id_sys_periode, id_data_pendaftaran } },
  );
  return response.data;
};

const generateTagihanPendaftaran = async (
  id_sys_periode: string,
  id_data_pendaftaran: string,
  payment_gateway_id: number | null,
) => {
  const response = await api.post(
    `/siswa/penerimaan/smbjm/validasi/generate-tagihan-daftar`,
    { id_sys_periode, id_data_pendaftaran, payment_gateway_id },
  );
  return response.data;
};
const getInvoice = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/validasi/invoice-tagihan?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data?.data;
};
const getStatusPembayaran = async (id_sys_periode: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/validasi/cek-status-pembayaran-daftar?id_sys_periode=${id_sys_periode}`,
  );
  return response.data;
};

export default function KartuPeserta() {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const { user } = useUser();
  const { apiBaseUrl } = useApiBaseUrl();
  const { dataPeriode } = useTimePeriode();

  const [showGenerateTagihan, setShowGenerateTagihan] = useState(false);
  const [showDetailTagihan, setShowDetailTagihan] = useState(false);
  const [showDetailPembayaran, setShowDetailPembayaran] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null);

  const { listPaymentMethodPlaza, isPendingListPaymentMethodPlaza } =
    usePaymentMethod({ enable: showGenerateTagihan });

  // Queries
  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataBiodata", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getBiodata(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user?.id && !!sessionPeriode?.id_sys_periode,
    });
  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;
  const {
    data: dataInformasiTagihan,
    isPending: isPendingDataInformasiTagihan,
  } = useQuery({
    queryKey: ["informasiTagihan", sessionPeriode?.id_sys_periode],
    queryFn: () => getInformasiTagihan(sessionPeriode?.id_sys_periode),
    enabled: !!sessionPeriode?.id_sys_periode,
  });

  const { data: dataTagihanExpired } = useQuery({
    queryKey: [
      "tagihanExpired",
      sessionPeriode?.id_sys_periode,
      dataPendaftaran,
    ],
    queryFn: () =>
      getTagihanExpired(
        sessionPeriode?.id_sys_periode,
        String(dataPendaftaran?.id_data_pendaftaran),
      ),
    enabled:
      !!sessionPeriode?.id_sys_periode &&
      !!dataPendaftaran &&
      !!dataInformasiTagihan &&
      !!showGenerateTagihan,
  });

  const {
    data: listDataInvoice,
    isPending: isPendingListDataInvoice,
    isFetched: isFetchedDataInvoice,
  } = useQuery({
    queryKey: ["listDataInvoice", dataPendaftaran?.id_data_pendaftaran],
    queryFn: () =>
      getInvoice(String(dataPendaftaran?.id_data_pendaftaran) || ""),
    enabled: !!dataPendaftaran?.id_data_pendaftaran,
  });
  const listInvoiceLunas = useMemo(() => {
    if (!listDataInvoice) return [];

    return listDataInvoice.filter(
      (invoice: DataTagihanDaftarPlaza) => invoice.status_bayar == 2,
    );
  }, [listDataInvoice]);

  const {
    data: dataStatusPembayaran,
    isLoading: isLoadingDataStatusPembayaran,
    isFetched: isFetchedDataStatusPembayaran,
  } = useQuery({
    queryKey: [
      "dataStatusPembayaran",
      sessionPeriode?.id_sys_periode,
      listDataInvoice,
    ],
    queryFn: () => getStatusPembayaran(sessionPeriode?.id_sys_periode),
    enabled:
      !!sessionPeriode?.id_sys_periode &&
      (!!listDataInvoice?.length || isFetchedDataInvoice),
  });

  const isSudahGenerateTagihan =
    isFetchedDataInvoice && listDataInvoice && listDataInvoice.length > 0;
  const isSudahBayar =
    isFetchedDataStatusPembayaran &&
    dataStatusPembayaran?.data &&
    dataStatusPembayaran?.data?.status_pembayaran === "SUDAH_BAYAR";

  const queryClient = useQueryClient();
  const {
    mutate: mutateGenerateTagihan,
    isPending: isPendingGenerateTagihan,
    error: errorGenerateTagihan,
  } = useMutation({
    mutationFn: () =>
      generateTagihanPendaftaran(
        sessionPeriode?.id_sys_periode || "",
        String(dataPendaftaran?.id_data_pendaftaran) || "",
        selectedPaymentMethod?.payment_gateway_id ?? null,
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataInvoice"] });
      toast.success(data?.message);
      setShowGenerateTagihan(false);
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      toast.error(
        error.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleGenerateTagihanPendaftaran = (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    mutateGenerateTagihan();
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Disalin ke clipboard!");
  };

  if (
    isPendingDataSiswa ||
    isPendingDataInformasiTagihan ||
    isPendingListDataInvoice
  ) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b pb-4 border-slate-200 dark:border-slate-800">
          <SkeletonRow className="w-10 h-10" />
          <div className="space-y-2">
            <SkeletonRow className="w-40 h-6" />
            <SkeletonRow className="w-60 h-4" />
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Card className="h-64">
            <SkeletonRow className="w-full h-full rounded-xl" />
          </Card>
          <Card className="h-64">
            <SkeletonRow className="w-full h-full rounded-xl" />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center gap-3 border-b pb-4 border-slate-200 dark:border-slate-800">
        <div className="p-2 bg-blue-100 rounded-lg text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
          <Ticket className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Kartu Peserta
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Status pembayaran dan informasi ujian Anda.
          </p>
        </div>
      </div>

      <Alert className="bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
        <Info className="h-4 w-4" />
        <AlertTitle className="font-bold">Wajib Diperhatikan!</AlertTitle>
        <AlertDescription className="text-xs">
          Hanya peserta yang telah melakukan{" "}
          <span className="font-bold">
            Pembayaran dan mencetak Kartu Peserta
          </span>{" "}
          yang akan diikutkan dalam proses seleksi.
        </AlertDescription>
      </Alert>

      <div className="grid gap-6 md:grid-cols-2 grid-cols-1">
        {/* --- CARD 1: STATUS PEMBAYARAN --- */}
        <Card className="h-full flex flex-col relative overflow-hidden">
          {isLoadingDataStatusPembayaran && (
            <div className="absolute inset-0 bg-white/80 dark:bg-slate-950/80 z-10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-600" /> Pembayaran
            </h2>
            {!isLoadingDataStatusPembayaran &&
              (isSudahBayar ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200">
                  Lunas
                </Badge>
              ) : (
                <Badge variant="destructive">Belum Lunas</Badge>
              ))}
          </div>

          <div className="flex-1 space-y-4">
            <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-slate-500 dark:text-slate-400 shrink-0">
                  Jalur Pendaftaran
                </span>
                <span className="font-medium text-right text-slate-900 dark:text-slate-100 break-words">
                  {dataPeriode?.nama_jalur_jenis}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-500 dark:text-slate-400 shrink-0">
                  Nama Lengkap
                </span>
                <span className="font-medium text-right text-slate-900 dark:text-slate-100 break-words">
                  {dataSiswa?.nama_lengkap}
                </span>
              </div>
              {isSudahBayar && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Nomor Pendaftaran</span>
                  <span className="font-medium">
                    {dataPendaftaran?.nomor_pendaftaran}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">Biaya Pendaftaran</span>
                <span className="font-medium">
                  {formatRupiah(
                    Number(dataInformasiTagihan?.biaya_daftar ?? 0),
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Biaya Portofolio</span>
                <span className="font-medium">
                  {formatRupiah(
                    Number(dataInformasiTagihan?.biaya_portofolio ?? 0),
                  )}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between text-base">
                <span className="font-bold">Total</span>
                <span className="font-bold text-blue-600">
                  {formatRupiah(
                    Number(dataInformasiTagihan?.total_tagihan ?? 0),
                  )}
                </span>
              </div>
            </div>

            {!isLoadingDataStatusPembayaran && (
              <>
                {!isSudahBayar ? (
                  <div className="flex flex-col gap-2">
                    {!isSudahGenerateTagihan ? (
                      <Button
                        className="w-full"
                        onClick={() => setShowGenerateTagihan(true)}
                      >
                        <CreditCard className="mr-2 h-4 w-4" /> Buat Tagihan
                      </Button>
                    ) : (
                      <div className="flex flex-wrap gap-3">
                        <Button
                          variant={"outline"}
                          className="w-full"
                          onClick={() => setShowGenerateTagihan(true)}
                        >
                          <CreditCard className="mr-2 h-4 w-4" /> Buat Tagihan
                          Ulang
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() =>
                            window.open(
                              `${apiBaseUrl}/siswa/penerimaan/smbjm/pendaftaran/slip-pembayaran?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
                            )
                          }
                        >
                          <Download className="mr-2 h-4 w-4" /> Unduh Tagihan
                        </Button>
                        <Button
                          className="w-full"
                          onClick={() => setShowDetailTagihan(true)}
                        >
                          Lihat Tagihan
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        window.open(
                          `${apiBaseUrl}/siswa/penerimaan/smbjm/pendaftaran/slip-pembayaran?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
                        )
                      }
                    >
                      <Download className="mr-2 h-4 w-4" /> Unduh Bukti Bayar
                    </Button>
                    <Button
                      className="w-full"
                      onClick={() => setShowDetailPembayaran(true)}
                    >
                      Lihat Bukti Pembayaran
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>

        {/* --- CARD 2: KARTU PESERTA & LOKASI --- */}
        <Card className="h-full flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-600" /> Cetak Kartu
            </h2>
            {!isLoadingDataStatusPembayaran &&
              (isSudahBayar ? (
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200">
                  Siap Cetak
                </Badge>
              ) : (
                <Badge variant="outline" className="text-slate-500">
                  Terkunci
                </Badge>
              ))}
          </div>

          <div className="flex-1 flex flex-col justify-center items-center text-center space-y-4 p-4">
            {!isLoadingDataStatusPembayaran &&
              (!isSudahBayar ? (
                <div className="text-slate-500 flex flex-col items-center py-6">
                  <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-full mb-3">
                    <CreditCard className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-sm font-medium">
                    Selesaikan pembayaran terlebih dahulu.
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Kartu peserta akan tersedia setelah pembayaran
                    terverifikasi.
                  </p>
                </div>
              ) : (
                <div className="w-full space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  {/* Visual Card / Success Banner */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50/30 dark:from-green-950/20 dark:to-emerald-900/10 border border-green-100 dark:border-green-900/50 rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden">
                    {/* Decorative Background Elements */}
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-green-200/50 dark:bg-green-800/30 rounded-full blur-2xl"></div>
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-20 h-20 bg-emerald-200/50 dark:bg-emerald-800/30 rounded-full blur-2xl"></div>

                    {/* Ikon Sukses */}
                    <div className="relative z-10 w-16 h-16 bg-white dark:bg-green-950 rounded-2xl shadow-sm border border-green-100 dark:border-green-800 flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-500 dark:text-green-400" />
                    </div>

                    {/* Teks Status */}
                    <div className="relative z-10 space-y-1.5">
                      <Badge
                        variant="outline"
                        className="bg-white/50 dark:bg-black/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/60 mb-2"
                      >
                        Pembayaran Terverifikasi
                      </Badge>
                      <h3 className="font-bold text-slate-900 dark:text-slate-100 text-lg">
                        Kartu Peserta Siap
                      </h3>
                      <p className="text-xs text-slate-600 dark:text-slate-400 px-4 leading-relaxed">
                        Silakan unduh dan cetak kartu peserta.
                      </p>
                    </div>
                  </div>

                  {/* Tombol Download Kartu (Primary Action) */}
                  <div className="space-y-3">
                    <Button
                      className="w-full h-14 text-base font-semibold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white"
                      onClick={() => {
                        localStorage.setItem(
                          "is_download_kartu_peserta_prestasi",
                          "true",
                        );
                        window.open(
                          `${apiBaseUrl}/siswa/penerimaan/smbjm/kartu-peserta/prestasi?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
                          "_blank", // <-- Tambahkan _blank agar tidak menimpa halaman saat ini
                        );
                      }}
                    >
                      <Printer className="mr-2.5 h-5 w-5" />
                      Download Kartu Peserta
                    </Button>

                    <p className="text-[11px] text-slate-400 dark:text-slate-500 flex items-center justify-center gap-1.5">
                      <Info className="w-3.5 h-3.5" />
                      Cetak menggunakan kertas ukuran A4.
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </Card>
      </div>

      {/* --- MODAL GENERATE TAGIHAN --- */}
      <Modal
        show={showGenerateTagihan}
        onClose={() => setShowGenerateTagihan(false)}
      >
        {isSudahGenerateTagihan && dataTagihanExpired?.status === false ? (
          // Tampilan peringatan jika tagihan belum expired
          <div className="flex flex-col items-center justify-center p-4 sm:p-6 text-center space-y-4">
            <div className="p-4 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-500 rounded-full mb-2">
              <Clock className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              Tagihan Belum Kedaluwarsa
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {dataTagihanExpired?.message ||
                "Anda tidak dapat membuat tagihan ulang karena tagihan Anda saat ini masih aktif. Silakan selesaikan pembayaran menggunakan tagihan yang sudah ada atau tunggu hingga batas waktu tagihan tersebut habis."}
            </p>
            <Button
              className="w-full mt-2"
              variant="outline"
              onClick={() => setShowGenerateTagihan(false)}
            >
              Kembali
            </Button>
          </div>
        ) : (
          // Tampilan form pemilihan bank jika tagihan sudah expired atau baru pertama kali buat
          <>
            <FormHeader
              title="Pilih Metode Pembayaran"
              icon={<CreditCard />}
              description="Pilih channel pembayaran yang Anda gunakan."
            />
            <form
              onSubmit={handleGenerateTagihanPendaftaran}
              className="mt-4 space-y-4"
            >
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                {isPendingListPaymentMethodPlaza ? (
                  <div className="space-y-2">
                    <SkeletonRow />
                    <SkeletonRow />
                  </div>
                ) : (
                  listPaymentMethodPlaza?.map((method: PaymentMethod) => (
                    <div
                      key={method.id}
                      onClick={() => setSelectedPaymentMethod(method)}
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all hover:bg-slate-50 dark:hover:bg-slate-800 ${
                        selectedPaymentMethod?.id === method.id
                          ? "border-blue-600 bg-blue-50/50 ring-1 ring-blue-600"
                          : "border-slate-200 dark:border-slate-700"
                      }`}
                    >
                      <div className="w-12 h-8 bg-white rounded border p-1 flex items-center justify-center mr-3">
                        <img
                          src={method.logo}
                          alt={method.name}
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                      <span className="font-medium text-sm">{method.name}</span>
                      {selectedPaymentMethod?.id === method.id && (
                        <CheckCircle2 className="ml-auto w-5 h-5 text-blue-600" />
                      )}
                    </div>
                  ))
                )}
              </div>
              <InputError
                text={errorGenerateTagihan?.response?.data?.errors?.payment_gateway_id?.join(
                  ",",
                )}
              />
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowGenerateTagihan(false)}
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isPendingGenerateTagihan || !selectedPaymentMethod}
                >
                  {isPendingGenerateTagihan ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Buat Tagihan"
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </Modal>

      {/* --- MODAL DETAIL TAGIHAN (INVOICE) --- */}
      <Modal
        show={showDetailTagihan}
        onClose={() => setShowDetailTagihan(false)}
        className="md:w-[32rem]"
      >
        <div className="flex flex-col h-full max-h-[85vh]">
          {/* HEADER MODAL - Tetap (Sticky) */}
          <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  Rincian Pembayaran
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Silakan lakukan pembayaran sesuai instruksi.
                </p>
              </div>
              <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-full">
                <Banknote className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          {/* BODY MODAL - Scrollable */}
          <div className="p-4 sm:p-6 overflow-y-auto space-y-5">
            {listDataInvoice?.length > 0 ? (
              listDataInvoice.map((invoice: DataTagihanDaftarPlaza) => {
                const bank = listPaymentMethodPlaza?.find(
                  (m: PaymentMethod) => m.id == invoice.payment_gateway_id,
                );

                return (
                  <div
                    key={invoice.id_data_tagihan_daftar_plaza}
                    className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm"
                  >
                    {/* Bagian Atas Kartu: Bank & Status */}
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-4 border-b border-slate-200 dark:border-slate-700 flex flex-wrap gap-3 items-center justify-between">
                      <div className="flex items-center gap-3">
                        {bank?.logo ? (
                          <div className="bg-white p-1 rounded border border-slate-200">
                            <img
                              src={bank.logo}
                              alt={bank.name}
                              className="h-6 object-contain"
                            />
                          </div>
                        ) : null}
                        <span className="font-semibold text-sm text-slate-700 dark:text-slate-300">
                          {bank?.name || "Metode Pembayaran"}
                        </span>
                      </div>
                      {(() => {
                        const statusInfo = getPaymentStatusInfo(
                          invoice.status_bayar,
                        );

                        return (
                          <Badge
                            variant="outline" // Kita pakai outline agar border kustom dari helper bekerja dengan baik
                            className={`px-2.5 py-0.5 shadow-none font-medium transition-colors ${statusInfo.className}`}
                          >
                            {statusInfo.label}
                          </Badge>
                        );
                      })()}
                    </div>

                    {/* Bagian Tengah Kartu: Nominal & VA */}
                    <div className="p-4 space-y-5 bg-white dark:bg-slate-900">
                      <div>
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">
                          Total Tagihan
                        </p>
                        <div className="text-2xl font-bold text-slate-900 dark:text-white">
                          {formatRupiah(Number(invoice.amount))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">
                          Nomor Virtual Account
                        </p>
                        <div className="flex gap-2">
                          <div className="flex-1 bg-slate-50 dark:bg-slate-800 p-3 rounded-lg font-mono text-lg font-bold tracking-widest text-center border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200">
                            {invoice.nomor_tagihan}
                          </div>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-auto w-12 shrink-0 border-slate-200 hover:bg-slate-100 hover:text-blue-600 dark:border-slate-700"
                            onClick={() =>
                              copyToClipboard(invoice.nomor_tagihan)
                            }
                            title="Salin Nomor VA"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Bagian Bawah Kartu: Batas Waktu */}
                      <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-col gap-3">
                        {/* Baris Batas Pembayaran */}
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500">
                            Batas Pembayaran
                          </span>
                          <span className="text-sm font-medium text-red-600 dark:text-red-400">
                            {formatDateIndonesia(invoice.expired_date)}
                          </span>
                        </div>

                        {/* Tombol Panduan Pembayaran */}
                        <a
                          href={web.linkProsedurPembayaran}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center w-full py-2.5 px-4 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 hover:bg-blue-100 rounded-lg dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/40 transition-colors group"
                        >
                          <HelpCircle className="w-4 h-4 mr-1.5" />
                          Lihat Panduan Pembayaran
                          <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-50 group-hover:opacity-100 transition-opacity" />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-slate-500">
                Tidak ada tagihan yang tersedia.
              </div>
            )}
          </div>

          {/* FOOTER MODAL - Tetap (Sticky) */}
          <div className="p-4 sm:p-6 border-t border-slate-100 dark:border-slate-800 shrink-0 bg-white dark:bg-slate-950 rounded-b-lg">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => setShowDetailTagihan(false)}
            >
              Tutup
            </Button>
          </div>
        </div>
      </Modal>

      {/* --- MODAL BUKTI PEMBAYARAN LUNAS --- */}
      <Modal
        show={showDetailPembayaran}
        onClose={() => setShowDetailPembayaran(false)}
        className="md:w-[32rem] p-0 overflow-hidden bg-white dark:bg-slate-950"
      >
        {/* Header Resi (Bagian Atas) */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-6 flex flex-col items-center justify-center border-b border-emerald-100 dark:border-emerald-800/50 text-center relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500"></div>
          <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-800/50 rounded-full flex items-center justify-center mb-3">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">
            Pembayaran Berhasil
          </h2>
          <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">
            Terima kasih, kewajiban pembayaran Anda telah lunas.
          </p>
        </div>

        {/* Detail Resi (Bagian Tengah) */}
        <div className="p-6">
          {listInvoiceLunas && listInvoiceLunas.length > 0 ? (
            <div className="space-y-6">
              {listInvoiceLunas.map(
                (invoice: DataTagihanDaftarPlaza, index: number) => {
                  // Cari data metode pembayaran (bank) dari list
                  const bank = listPaymentMethodPlaza?.find(
                    (m: PaymentMethod) => m.id == invoice.payment_gateway_id,
                  );

                  return (
                    <div
                      key={invoice.id_data_tagihan_daftar_plaza || index}
                      className="space-y-4"
                    >
                      {/* Highlight Nominal */}
                      <div className="flex flex-col items-center justify-center py-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                        <span className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                          Total Dibayar
                        </span>
                        <span className="text-3xl font-black text-slate-900 dark:text-white">
                          {formatRupiah(Number(invoice.amount))}
                        </span>
                      </div>

                      {/* List Informasi */}
                      <div className="space-y-3 text-sm">
                        {/* Nomor Referensi/VA */}
                        <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800/60">
                          <span className="text-slate-500 flex items-center gap-2">
                            <Receipt className="w-4 h-4" /> Nomor Tagihan
                          </span>
                          <span className="font-semibold text-slate-900 dark:text-slate-100">
                            {invoice.nomor_tagihan}
                          </span>
                        </div>

                        {/* Metode Pembayaran */}
                        <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800/60">
                          <span className="text-slate-500 flex items-center gap-2">
                            <CreditCard className="w-4 h-4" /> Metode Bayar
                          </span>
                          <div className="flex items-center gap-2">
                            {bank?.logo && (
                              <img
                                src={bank.logo}
                                alt={bank.name}
                                className="h-4 object-contain"
                              />
                            )}
                            <span className="font-semibold text-slate-900 dark:text-slate-100">
                              {bank?.name || "Transfer Bank"}
                            </span>
                          </div>
                        </div>

                        {/* Tanggal Pembayaran */}
                        <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800/60">
                          <span className="text-slate-500 flex items-center gap-2">
                            <Calendar className="w-4 h-4" /> Tanggal Lunas
                          </span>
                          <span className="font-medium text-slate-900 dark:text-slate-100 text-right">
                            {/* Gunakan payment_date jika ada, jika null fallback ke updated_at */}
                            {invoice.payment_date
                              ? formatDateIndonesia(
                                  invoice.payment_date,
                                  "full",
                                )
                              : ""}
                          </span>
                        </div>

                        {/* Keterangan Tagihan */}
                        <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800/60">
                          <span className="text-slate-500 flex items-center gap-2">
                            <FileText className="w-4 h-4" /> Keterangan
                          </span>
                          <span className="font-medium text-slate-900 dark:text-slate-100 text-right max-w-[60%]">
                            {invoice.keterangan || "Biaya Pendaftaran"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          ) : (
            // Fallback jika anehnya modal terbuka tapi listInvoiceLunas kosong
            <div className="text-center py-8 text-slate-500">
              Data pembayaran tidak ditemukan.
            </div>
          )}

          {/* Tombol Aksi Bawah */}
          <div className="mt-8 flex gap-3">
            <Button
              className="flex-1 bg-slate-100 text-slate-700 hover:bg-slate-200 border-none dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              variant="outline"
              onClick={() => setShowDetailPembayaran(false)}
            >
              Tutup
            </Button>
            {/* Opsional: Tombol Unduh Invoice jika API mendukung */}
            <Button
              className="flex-1"
              onClick={() =>
                window.open(
                  `${apiBaseUrl}/siswa/penerimaan/smbjm/pendaftaran/slip-pembayaran?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
                )
              }
            >
              Unduh Bukti
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
